// provider-config-api.ts — typed client for the global provider-routing config
// REST API (migration 009). Admin-only endpoints read + update the singleton
// mp_provider_config row: the OpenAI master on/off, the routing mode, and the
// per-provider model map. All manager-native (NOT proxied to the agent
// container) — identity comes from the Auth0 bearer — so we reuse the same
// base-url + auth-header helpers as lib/api.ts / session-flag-api.ts.

import { API_ORIGIN, jsonOrThrow, authHeaders } from './api';

async function jsonHeaders(): Promise<Record<string, string>> {
	return { ...(await authHeaders()), 'content-type': 'application/json' };
}

// ── DTOs (mirror backend/manager/src/provider-config.ts) ──────────────────────

// The stored routing mode (back-compat). The UI no longer sets this directly — it uses
// `defaultProvider` (sugar over claude_only/openai_only). `prefer_*` predates the
// agnostic redesign and is treated as the matching `*_only`.
export type RoutingMode = 'claude_only' | 'openai_only' | 'prefer_claude' | 'prefer_openai';

// Which provider a NEW chat defaults to (the agnostic, UI-facing control).
export type DefaultProvider = 'claude' | 'openai';

// The per-provider model map + effort map + fallback flag (the jsonb
// `routing_config` blob). An absent effort means the agent's built-in default.
export type RoutingConfig = {
	models?: { claude?: string; openai?: string };
	efforts?: { claude?: string; openai?: string };
	fallbackEnabled?: boolean;
	// Codex "Fast" service tier (OpenAI-only). DEFAULT ON — an absent flag means ON, so
	// the UI binds `fastMode ?? true`. Lower latency with increased ChatGPT-subscription credit usage.
	fastMode?: boolean;
	// CLAUDE_ACCOUNTS ids (AccountCfg.id, e.g. "1"/"2") excluded from NEW-chat account
	// selection — the admin's way to park an account that's at its weekly cap. Absent/[]
	// = every configured account is selectable. Existing chats stay pinned to the account
	// they were started on; this only steers NEW chats. The backend fails OPEN (disabling
	// every account is ignored) so a chat always has somewhere to run.
	disabledClaudeAccounts?: string[];
};

// The singleton provider config. Provider-agnostic: independent enable flags for BOTH
// providers + a `defaultProvider`. `updatedAt` is epoch-ms (the backend serializes its
// timestamptz to a number).
export type ProviderConfig = {
	claudeEnabled: boolean;
	openaiEnabled: boolean;
	defaultProvider: DefaultProvider;
	routingMode: RoutingMode; // storage-canonical; UI binds defaultProvider
	routingConfig: RoutingConfig;
	updatedBy: string | null;
	updatedAt: number;
};

// A partial update — any subset of the mutable fields. `defaultProvider` is sugar over
// `routingMode` (send whichever; the backend maps defaultProvider → routingMode).
export type ProviderConfigPatch = {
	claudeEnabled?: boolean;
	openaiEnabled?: boolean;
	defaultProvider?: DefaultProvider;
	routingMode?: RoutingMode;
	routingConfig?: RoutingConfig;
};

// ── Admin endpoints ───────────────────────────────────────────────────────────

// Current global provider config (renders the Providers tab).
export async function getProviderConfig(): Promise<ProviderConfig> {
	const res = await fetch(`${API_ORIGIN}/admin/provider-config`, { headers: await authHeaders() });
	const body = await jsonOrThrow<{ config: ProviderConfig }>(res, 'getProviderConfig');
	return body.config;
}

// Update the global provider config. The server is the source of truth — the
// returned DTO is authoritative, so the caller assigns it rather than flipping
// local state first.
export async function setProviderConfig(patch: ProviderConfigPatch): Promise<ProviderConfig> {
	const res = await fetch(`${API_ORIGIN}/admin/provider-config`, {
		method: 'PUT',
		headers: await jsonHeaders(),
		body: JSON.stringify(patch),
	});
	const body = await jsonOrThrow<{ config: ProviderConfig }>(res, 'setProviderConfig');
	return body.config;
}

// ── Mid-chat provider switch on usage-limit exhaustion (per-session) ───────────
// NOT admin — identity is the caller's bearer, and the manager scopes the switch to
// the caller's OWN chat. The offer is the mid-chat extension of the new-chat fallback.

export type SwitchOffer =
	| { available: false }
	| { available: true; to: 'claude' | 'codex'; label: string; kind?: 'provider' | 'account' };

// Can a chat currently running on `from` switch to the other provider to continue?
// (Gated on the admin Fallback toggle + the target provider being serviceable.)
export async function getSwitchOffer(
	sessionId: string,
	from: 'claude' | 'codex',
	reason: 'usage_limit' | 'overloaded' | 'auth_error' = 'usage_limit',
): Promise<SwitchOffer> {
	const res = await fetch(`${API_ORIGIN}/sessions/${sessionId}/switch-offer?from=${from}&reason=${reason}`, {
		headers: await authHeaders(),
	});
	return jsonOrThrow<SwitchOffer>(res, 'getSwitchOffer');
}

// Flip a chat IN PLACE onto `to` (same session + worktree; files preserved). The
// caller re-sends the failed message afterward so it runs on the new provider.
export async function switchProvider(
	sessionId: string,
	to: 'claude' | 'codex',
	reason: 'usage_limit' | 'overloaded' | 'auth_error' = 'usage_limit',
): Promise<{ ok: boolean; to: 'claude' | 'codex'; model: string }> {
	const res = await fetch(`${API_ORIGIN}/sessions/${sessionId}/switch-provider`, {
		method: 'POST',
		headers: await jsonHeaders(),
		// `reason` is the analytics label only (the provider_switched event); it does
		// not change the switch mechanics, which are identical for both codes.
		body: JSON.stringify({ to, reason }),
	});
	return jsonOrThrow(res, 'switchProvider');
}

// ── OpenAI/Codex subscription rate limits (read from codex rollout files) ──────
// One rolling window (`primary` = 5h) + the weekly window (`secondary`), each a
// used-percent + reset time. Per-subscription (shared), so it's the freshest
// snapshot any user's Codex turn produced. null until a Codex turn has run.
export type RateWindow = {
	usedPercent: number;
	windowMinutes: number;
	resetsAt: number; // epoch SECONDS
};
export type OpenAiUsage = {
	planType: string | null;
	primary: RateWindow | null;
	secondary: RateWindow | null;
	capturedAt: number; // epoch ms
	sourceUser: string;
	live: boolean; // true = on-demand wham/usage; false = last-Codex-turn rollout
} | null;

export async function getProviderUsage(): Promise<OpenAiUsage> {
	const res = await fetch(`${API_ORIGIN}/admin/provider-usage`, { headers: await authHeaders() });
	const body = await jsonOrThrow<{ usage: OpenAiUsage }>(res, 'getProviderUsage');
	return body.usage;
}

// ── Claude (Anthropic Max) per-sub usage (ERP /backend/claude-usage projection) ─────
// Buckets are utilization 0-100 + an ISO reset time (different units from the OpenAI
// RateWindow). `weekly` already folds the opus??aggregate caveat server-side.
export type ClaudeBucket = { utilization: number; resets_at: string | null } | null;

// A per-MODEL weekly cap (Anthropic's `limits[].kind === "weekly_scoped"`). These sit
// BESIDE the aggregate weekly bucket and routinely bind first: an account can read 64%
// weekly overall while Fable alone is at 98%, so showing only `weekly` under-reports the
// limit the user actually hits. `key` is a slug of `label` (unique per account → a safe
// {#each} key); `severity` is upstream's own grading ("normal" | "warning" | "critical").
export type ClaudeScopedLimit = {
	key: string;
	label: string;
	utilization: number;
	resets_at: string | null;
	severity: string | null;
};

export type ClaudeAccountUsage = {
	// Unique per row — the only safe {#each} key (labels can collide; keying on them
	// crashed the app shell with each_key_duplicate, Jun 2026). Optional only to
	// tolerate a stale manager during the deploy-skew window; fall back to the index.
	id?: string;
	label: string;
	subscriptionType: string | null;
	fiveHour: ClaudeBucket;
	weekly: ClaudeBucket;
	// Per-model weekly caps for this account; [] when upstream reports none.
	scoped: ClaudeScopedLimit[];
	// The CLAUDE_ACCOUNTS id this usage row maps to (joined by email), or null when the
	// row couldn't be attributed to a configured routing account. Optional on the wire:
	// an OLD manager (deploy skew) omits it entirely, which is NOT the same as "null =
	// definitely not routed" — the UI must treat `undefined` as "unknown" and say nothing.
	routingId?: string | null;
	// Server-computed mirror of the roster entry's `enabled` for this row. The panel
	// binds its switches to `routingAccounts` (the config roster) instead — a usage row
	// is telemetry and can go missing; the roster can't.
	routingEnabled?: boolean;
	error: string | null;
};

// ── The CONFIGURED routing roster (the CONTROL PLANE) ─────────────────────────
// One entry per manager CLAUDE_ACCOUNTS entry — ALWAYS present, whether or not the
// usage feed produced a row for it. This is what the admin's on/off switches bind to.
// Why not the usage feed: the feed is LOSSY (a per-account poll failure yields an error
// row with no email, so it can't be joined to a config entry), so a control derived from
// usage rows silently disappears for a configured, actively-routed account — and a
// disabled account could then never be re-enabled from any UI.
// `enabled` is computed server-side through the SAME activeAccounts() the router uses,
// so the panel can never claim "off" for an account the router is still picking.
export type ClaudeRoutingAccount = {
	id: string; // CLAUDE_ACCOUNTS id (AccountCfg.id, e.g. "1" / "2")
	label: string; // CLAUDE_ACCOUNTS label (AccountCfg.label, e.g. "sayano" / "blocher")
	enabled: boolean;
};

export type ClaudeUsage = {
	accounts: ClaudeAccountUsage[];
	// DEPLOY SKEW: the manager and the frontend are separate Coolify apps and deploy
	// independently, so a NEW frontend routinely talks to an OLD manager that sends
	// neither of these. They are OPTIONAL on purpose and defaulted at every read
	// (`?? []` / `?? false`): an absent roster must degrade to "no switches shown, no
	// routing claims made", never to a card confidently mislabelling every account.
	routingAccounts?: ClaudeRoutingAccount[];
	// True when activeAccounts() hit its FAIL-OPEN branch: every configured account is
	// disabled, so the router ignores the exclusion list and new chats still land on all
	// of them. The panel must SAY this rather than showing a row of misleading Off switches.
	failOpen?: boolean;
	fetchedAt: number;
	lastError: string | null;
} | null;

// Current per-sub Claude usage. `refresh=true` forces an on-demand ERP poll (bounded by
// the ERP's 300s cache); otherwise serves the ≤300s-fresh in-memory snapshot.
export async function getClaudeUsage(refresh = false): Promise<ClaudeUsage> {
	const url = `${API_ORIGIN}/admin/claude-usage${refresh ? '?refresh=1' : ''}`;
	const res = await fetch(url, { headers: await authHeaders() });
	const body = await jsonOrThrow<{ usage: ClaudeUsage }>(res, 'getClaudeUsage');
	return body.usage;
}

// ── OpenAI/Codex credential: device-code "Connect ChatGPT" (migration 010) ──────
// Mirrors backend/manager/src/provider-credentials.ts. The credential is sealed in
// the vault; the API only ever surfaces a source + non-secret meta, never the blob.

// Non-secret facts about the vaulted credential, shown in the Providers tab.
export type ProviderCredentialMeta = {
	accessExpiresAt?: number | null; // ms epoch
	planType?: string | null;
	accountId?: string | null;
	lastRefresh?: string | null;
};

// Where the effective credential comes from: 'db' = connected via the flow (vaulted),
// 'env' = the CODEX_AUTH_JSON fallback, 'missing' = neither.
export type CredentialStatus = {
	source: 'db' | 'env' | 'missing';
	meta: (ProviderCredentialMeta & { connectionStatus: 'connected' | 'needs_auth' | 'error' }) | null;
};

// The device-code login's progress. 'connected' is terminal-success; 'failed' terminal-error.
export type ConnectProgress =
	| { phase: 'starting' }
	| { phase: 'awaiting_authorization'; verificationUrl: string; userCode: string; expiresInMin: number | null }
	| { phase: 'connected'; meta: CredentialStatus['meta'] }
	| { phase: 'failed'; error: string };

export async function getCredentialStatus(): Promise<CredentialStatus> {
	const res = await fetch(`${API_ORIGIN}/admin/provider-credential`, { headers: await authHeaders() });
	const body = await jsonOrThrow<{ credential: CredentialStatus }>(res, 'getCredentialStatus');
	return body.credential;
}

// Begin the device-code login; the response carries the verification URL + one-time code.
export async function connectCredentialStart(): Promise<ConnectProgress> {
	const res = await fetch(`${API_ORIGIN}/admin/provider-credential/connect`, {
		method: 'POST',
		headers: await jsonHeaders(),
	});
	const body = await jsonOrThrow<{ progress: ConnectProgress }>(res, 'connectCredentialStart');
	return body.progress;
}

// Poll the login until 'connected' or 'failed' (vaults the credential on success).
export async function connectCredentialPoll(): Promise<ConnectProgress> {
	const res = await fetch(`${API_ORIGIN}/admin/provider-credential/connect`, { headers: await authHeaders() });
	const body = await jsonOrThrow<{ progress: ConnectProgress }>(res, 'connectCredentialPoll');
	return body.progress;
}

// Drop the vaulted credential (the CODEX_AUTH_JSON env fallback, if any, resumes).
export async function disconnectCredential(): Promise<CredentialStatus> {
	const res = await fetch(`${API_ORIGIN}/admin/provider-credential`, {
		method: 'DELETE',
		headers: await authHeaders(),
	});
	const body = await jsonOrThrow<{ credential: CredentialStatus }>(res, 'disconnectCredential');
	return body.credential;
}
